import { HttpClient, HttpContext } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import {
  defer,
  forkJoin,
  from,
  iif,
  lastValueFrom,
  Observable,
  of
} from "rxjs";
import { UserNotify } from "src/app/support/notify-decorator";
import { PageRequest } from "src/app/support/page-request";
import { PaginatedResult } from "src/app/support/paginated-result";
import { environment } from "src/environments/environment";
import {
  Quote,
  QuoteCreate,
  QuoteLight,
  QuotePrintViewModel,
  QuoteStatus
} from "../models/quote/quote";
import { catchError, map, shareReplay, switchMap } from "rxjs/operators";
import {
  compact,
  isNull,
  isUndefined,
  omitBy,
  pickBy,
  round,
  sum
} from "lodash-es";
import { dateUtil } from "src/app/support/date-util";
import { getCustomerNominative } from "src/app/support/customer";
import {
  FileNamingService,
  FileType
} from "src/app/services/file-naming.service";
import { ConfigurationBackend } from "src/app/backend/api/configuration.backend";
import { TemplateService } from "src/app/services/template.service";
import { PrintService } from "src/app/services/print/print.service";
import { formatAddress } from "src/app/common/format/address-format";
import { InvoiceBackend } from "src/app/backend/api/invoice.backend";
import { sntTranslate } from "src/app/support/translate";
import { sntConfirm } from "src/app/common/dialog";
import { CustomerBackend } from "src/app/backend/api/customer.backend";
import {
  QuoteItem,
  QuoteItemCreate
} from "src/app/backend/models/quote/quote.item";
import { blobToDataUrl } from "src/app/support/blob";
import { StorageBackend } from "src/app/backend/api/storage/storage.backend";
import { API } from "src/app/backend/api";
import { QuoteTemplateBackend } from "src/app/backend/api/quote-template.backend";
import { MedicalServiceBackend } from "src/app/backend/api/medical-service/medical-service.backend";
import { DrugBackend } from "src/app/backend/api/drug.backend";
import { ProductBackend } from "src/app/backend/api/product.backend";
import { LetterheadBackend } from "src/app/backend/api/letterhead/letterhead.backend";
import { formatCurrency } from "src/app/support/currency";
import { QuoteTemplateItem } from "src/app/backend/models/quote-template/quote-template-item";
import { MedicalServiceLight } from "src/app/backend/models/medical-service";
import { DrugLight } from "src/app/backend/models/drug";
import { ProductLight } from "src/app/backend/models/product";
import { BookItemBackend } from "src/app/backend/api/book-item.backend";
import { RateTableBackend } from "src/app/backend/api/rate-table/rate-table.backend";
import { RateTableLight } from "src/app/backend/models/rate-table/rate-table";
import {
  Regions,
  RegionService
} from "src/app/services/feature-access-control/region.service";
import { MODULES } from "src/app/core/modules";
import { ModuleService } from "src/app/services/feature-access-control/module.service";
import {
  AttachmentV2,
  AttachmentV2Create
} from "src/app/backend/models/attachment/attachment";
import { HTTP_CONTEXT_TOKEN_NO_CACHE } from "src/app/core/http-context-tokens";
import { PetLight } from "src/app/backend/models/pet/pet";
import { PetBackend } from "src/app/backend/api/pet.backend";
import { FiscalProfileBackend } from "src/app/backend/api/fiscal-profile.backend";

@Injectable()
export class QuoteBackend {
  private readonly http = inject(HttpClient);
  private readonly templateService = inject(TemplateService);
  private readonly printService = inject(PrintService);
  private readonly configurationBackend = inject(ConfigurationBackend);
  private readonly invoiceBackend = inject(InvoiceBackend);
  private readonly customerBackend = inject(CustomerBackend);
  private readonly storageBackend = inject(StorageBackend);
  private readonly quoteTemplateBackend = inject(QuoteTemplateBackend);
  private readonly medicalServiceBackend = inject(MedicalServiceBackend);
  private readonly drugBackend = inject(DrugBackend);
  private readonly productBackend = inject(ProductBackend);
  private readonly letterheadBackend = inject(LetterheadBackend);
  private readonly fiscalProfileBackend = inject(FiscalProfileBackend);
  private readonly bookItemBackend = inject(BookItemBackend);
  private readonly ratetableBackend = inject(RateTableBackend);
  private readonly petBackend = inject(PetBackend);

  private readonly moduleService = inject(ModuleService);
  private readonly regionService = inject(RegionService);
  private readonly fileNamingService = inject(FileNamingService);

  @UserNotify({
    successMessage: marker("Preventivo creato"),
    errorMessage: marker("Impossibile creare il preventivo")
  })
  create(payload: QuoteCreate, items?: QuoteItem[]): Observable<Quote> {
    return this.createSilently(payload, items);
  }

  @UserNotify({
    successMessage: marker("Preventivo creato"),
    errorMessage: marker("Impossibile creare il preventivo")
  })
  createFromTemplate(
    payload: QuoteCreate,
    templateId: string
  ): Observable<Quote> {
    return this.quoteTemplateBackend.load(templateId).pipe(
      switchMap((quoteTemplate) => {
        return quoteTemplate.items?.length > 0
          ? forkJoin(
              quoteTemplate.items!.map((quoteItem: QuoteTemplateItem) => {
                switch (quoteItem.type) {
                  case "medical-service":
                    return this.medicalServiceBackend
                      .loadLight(quoteItem.rateTableItemId)
                      .pipe(
                        catchError(() => of(undefined)),
                        switchMap(
                          (medicalService: MedicalServiceLight | undefined) => {
                            if (!medicalService) {
                              return of(undefined);
                            }

                            return this.ratetableBackend
                              .loadLight(medicalService.rateTableId)
                              .pipe(
                                map((rateTable: RateTableLight) => {
                                  return <QuoteItem>{
                                    physiciansPercentage:
                                      isUndefined(
                                        medicalService.physiciansPercentage
                                      ) ||
                                      isNull(
                                        medicalService.physiciansPercentage
                                      )
                                        ? rateTable.serviceModifier
                                            .physiciansPercentage
                                        : medicalService.physiciansPercentage,
                                    rateTableItemId: medicalService.id,
                                    number: medicalService.number,
                                    type: quoteItem.type,
                                    name: medicalService.name,
                                    description: medicalService.description,
                                    quantity: quoteItem.quantity,
                                    maxQuantity:
                                      quoteItem.maxQuantity ||
                                      quoteItem.quantity,
                                    unitPrice:
                                      quoteItem.unitPrice ||
                                      medicalService.unitPrice,
                                    maxUnitPrice:
                                      quoteItem.maxUnitPrice ||
                                      medicalService.maxUnitPrice ||
                                      medicalService.unitPrice,
                                    vat: medicalService.vat,
                                    rateable:
                                      quoteItem.unitPrice ||
                                      medicalService.unitPrice,
                                    vatNature: undefined,
                                    note: "",
                                    discount: quoteItem.discount || 0,
                                    vatTaxAmount: 0,
                                    taxedTotal: 0,
                                    adjustment: 0,
                                    adjustedTotal: 0
                                  };
                                })
                              );
                          }
                        )
                      );
                  case "drug-assignment":
                    return this.drugBackend
                      .loadLight(quoteItem.rateTableItemId)
                      .pipe(
                        map((drug: DrugLight) => {
                          return <QuoteItem>{
                            physiciansPercentage: 0,
                            rateTableItemId: drug.id,
                            type: quoteItem.type,
                            name: drug.name || "",
                            description: drug.description,
                            quantity: quoteItem.quantity,
                            maxQuantity:
                              quoteItem.maxQuantity || quoteItem.quantity,
                            unitPrice: quoteItem.unitPrice || drug.unitPrice,
                            maxUnitPrice:
                              quoteItem.maxUnitPrice || drug.unitPrice,
                            vat: drug.vat,
                            rateable: quoteItem.unitPrice || drug.unitPrice,
                            number: "number",
                            note: "",
                            vatNature: undefined,
                            discount: quoteItem.discount || 0,
                            vatTaxAmount: 0,
                            taxedTotal: 0,
                            adjustment: 0,
                            adjustedTotal: 0
                          };
                        })
                      );
                  case "parapharmaceutical":
                    return this.drugBackend
                      .loadLight(quoteItem.rateTableItemId)
                      .pipe(
                        map((drug: DrugLight) => {
                          return <QuoteItem>{
                            physiciansPercentage: 0,
                            rateTableItemId: drug.id,
                            type: quoteItem.type,
                            name: drug.name || "",
                            description: drug.description,
                            quantity: quoteItem.quantity,
                            maxQuantity:
                              quoteItem.maxQuantity || quoteItem.quantity,
                            unitPrice: quoteItem.unitPrice || drug.unitPrice,
                            maxUnitPrice:
                              quoteItem.maxUnitPrice || drug.unitPrice,
                            vat: drug.vat,
                            rateable: quoteItem.unitPrice || drug.unitPrice,
                            number: "number",
                            note: "",
                            vatNature: undefined,
                            discount: quoteItem.discount || 0,
                            vatTaxAmount: 0,
                            taxedTotal: 0,
                            adjustment: 0,
                            adjustedTotal: 0
                          };
                        })
                      );
                  case "product-sale":
                    return this.productBackend
                      .loadLight(quoteItem.rateTableItemId)
                      .pipe(
                        map((product: ProductLight) => {
                          return <QuoteItem>{
                            physiciansPercentage: 0,
                            rateTableItemId: product.id,
                            type: quoteItem.type,
                            number: product.number,
                            name: product.name,
                            description: product.description,
                            quantity: quoteItem.quantity,
                            maxQuantity:
                              quoteItem.maxQuantity || quoteItem.quantity,
                            unitPrice: quoteItem.unitPrice || product.unitPrice,
                            maxUnitPrice:
                              quoteItem.maxUnitPrice ||
                              product.maxUnitPrice ||
                              product.unitPrice,
                            vat: product.vat,
                            rateable: quoteItem.unitPrice || product.unitPrice,
                            note: "",
                            vatNature: undefined,
                            discount: quoteItem.discount || 0,
                            vatTaxAmount: 0,
                            taxedTotal: 0,
                            adjustment: 0,
                            adjustedTotal: 0
                          };
                        })
                      );
                }
              })
            ).pipe(
              map((items) => compact(items)),
              switchMap((items: QuoteItem[]) => {
                return this.createSilently(payload, items);
              })
            )
          : this.createSilently(payload);
      })
    );
  }

  private createSilently(
    payload: QuoteCreate,
    items?: QuoteItem[]
  ): Observable<Quote> {
    return this.http.post<Quote>(`${environment.snoots.api.billing}/quotes`, {
      ...payload,
      items: items
    });
  }

  @UserNotify({
    successMessage: marker("Righe aggiunte al conto"),
    errorMessage: marker("Impossibile le righe del preventivo al conto")
  })
  addToBook(id: string, bookId: string): Observable<Quote> {
    return this.http.post<Quote>(
      `${environment.snoots.api.billing}/quotes/${id}/books/${bookId}`,
      {
        [id]: {
          price: 0,
          quantity: 0
        }
      }
    );
  }

  loadNoCache(id: string): Observable<Quote> {
    return this.load(
      id,
      new HttpContext().set(HTTP_CONTEXT_TOKEN_NO_CACHE, true)
    );
  }

  load(id: string, context?: HttpContext): Observable<Quote> {
    return this.http
      .get<
        Omit<Quote, "items"> & {
          items: Array<QuoteItem & { bookItemId?: string }>;
        }
      >(`${environment.snoots.api.billing}/quotes/${id}`)
      .pipe(
        map((quote) => {
          return {
            ...quote,
            items: quote.items?.map((x) => ({
              ...x,
              bookItemIds: x.bookItemIds
                ? x.bookItemIds
                : compact([x.bookItemId])
            }))
          };
        }),
        switchMap((quote: Quote) =>
          iif(
            () => !quote.items || quote.items.length === 0,
            of(quote),
            defer(() => {
              return forkJoin(
                quote.items!.map((item) =>
                  forkJoin({
                    invoice: iif(
                      () => !!item.invoiceId,
                      defer(() =>
                        this.invoiceBackend.load(item.invoiceId!).pipe(
                          catchError(() => {
                            return of({
                              id: undefined,
                              isDraft: true,
                              number: undefined
                            });
                          })
                        )
                      ),
                      of({
                        id: undefined,
                        isDraft: true,
                        number: undefined
                      })
                    ),
                    bookItems: iif(
                      () =>
                        !!quote.patient?.id &&
                        (item.bookItemIds?.length || 0) > 0,
                      defer(() => {
                        return forkJoin(
                          item.bookItemIds!.map((bookItemId: string) =>
                            this.bookItemBackend.load({
                              patientiId: quote.patient!.id,
                              itemId: bookItemId
                            })
                          )
                        );
                      }),
                      of([])
                    )
                  }).pipe(
                    map(({ invoice, bookItems }) => {
                      return {
                        item,
                        invoice,
                        bookItems
                      };
                    })
                  )
                )
              ).pipe(
                map((itemsWithInvoices) => {
                  return {
                    ...quote,
                    items:
                      itemsWithInvoices?.map((x) => ({
                        ...x.item,
                        invoiceId: x.invoice?.id,
                        invoiceNumber: x.invoice.number,
                        invoiceIsDraft: x.invoice.isDraft,
                        executedQuantity: sum(
                          x.bookItems?.map((x) => x?.quantity) || []
                        )
                      })) || []
                  };
                })
              );
            })
          )
        )
      )
      .pipe(map(extendDoc));
  }

  loadLight(id: string): Observable<QuoteLight> {
    return this.http
      .get<Quote>(`${environment.snoots.api.billing}/quotes/${id}`, {
        params: {
          light: true
        }
      })
      .pipe(map((response) => extendLight(response)));
  }

  list(
    request: PageRequest,
    filters: {
      number?: string | undefined;
      customerName?: string | undefined;
      status?: string | undefined;
      createdAtTo?: string | undefined;
      createdAtFrom?: string | undefined;
    }
  ): Observable<PaginatedResult<Quote>> {
    return this.http
      .get<PaginatedResult<Quote>>(`${environment.snoots.api.billing}/quotes`, {
        params: pickBy(<any>{
          p: request.page,
          s: request.pageSize,
          number: filters.number,
          customerName: filters.customerName,
          status: filters.status,
          createdAtTo: filters.createdAtTo,
          createdAtFrom: filters.createdAtFrom
        })
      })
      .pipe(
        map((result) => {
          return {
            ...result,
            docs: result.docs.map(extendDoc)
          };
        })
      );
  }

  listByCustomer(
    customerId: string,
    request: PageRequest
  ): Observable<PaginatedResult<Quote>> {
    return this.http
      .get<PaginatedResult<Quote>>(`${environment.snoots.api.billing}/quotes`, {
        params: {
          customerId: customerId,
          p: request.page,
          s: request.pageSize
        }
      })
      .pipe(
        map((result) => {
          return {
            ...result,
            docs: result.docs.map(extendDoc)
          };
        })
      );
  }

  listByCustomerLight(
    customerId: string,
    request: PageRequest
  ): Observable<PaginatedResult<QuoteLight>> {
    return this.http
      .get<PaginatedResult<QuoteLight>>(
        `${environment.snoots.api.billing}/quotes`,
        {
          params: {
            customerId: customerId,
            p: request.page,
            s: request.pageSize,
            light: true
          }
        }
      )
      .pipe(
        map((response) => {
          return {
            ...response,
            docs: response.docs.map(extendLight)
          };
        })
      );
  }

  listByPatient(
    patientId: string,
    request: PageRequest
  ): Observable<PaginatedResult<Quote>> {
    return this.http
      .get<PaginatedResult<Quote>>(
        `${environment.snoots.api.billing}/patients/${patientId}/quotes`,
        {
          params: {
            p: request.page,
            s: request.pageSize
          }
        }
      )
      .pipe(
        map((result) => {
          return {
            ...result,
            docs: result.docs.map(extendDoc)
          };
        })
      );
  }

  listAllByPatient(patientId: string): Observable<Quote[]> {
    return this.listByPatient(patientId, {
      page: 1,
      pageSize: 10000
    }).pipe(map((response) => response.docs));
  }

  listByPatientLight(
    patientId: string,
    request: PageRequest
  ): Observable<PaginatedResult<QuoteLight>> {
    return this.http
      .get<PaginatedResult<QuoteLight>>(
        `${environment.snoots.api.billing}/patients/${patientId}/quotes`,
        {
          params: {
            p: request.page,
            s: request.pageSize,
            light: true
          }
        }
      )
      .pipe(
        map((response) => {
          return {
            ...response,
            docs: response.docs.map(extendLight)
          };
        })
      );
  }

  listByRecoveryLight({
    recoveryId
  }: {
    recoveryId: string;
  }): Observable<QuoteLight[]> {
    return this.http
      .get<PaginatedResult<QuoteLight>>(
        `${environment.snoots.api.billing}/recoveries/${recoveryId}/quotes`,
        {
          params: {
            p: 1,
            s: 10000,
            light: true
          }
        }
      )
      .pipe(
        map((response) => {
          return response.docs.map(extendLight);
        })
      );
  }

  @UserNotify({
    successMessage: marker("Preventivo aggiornato"),
    errorMessage: marker("Impossibile aggiornare il preventivo")
  })
  update(id: string, request: Partial<Quote>): Observable<void> {
    return this.updateSilently(id, request);
  }

  updateSilently(id: string, request: Partial<Quote>): Observable<void> {
    return this.http.patch<void>(
      `${environment.snoots.api.billing}/quotes/${id}`,
      request
    );
  }

  @UserNotify({
    successMessage: marker("Preventivo clonato"),
    errorMessage: marker("Impossibile clonare il preventivo")
  })
  clone(id: string): Observable<Quote> {
    return this.http.post<Quote>(
      `${environment.snoots.api.billing}/quotes/${id}/clones`,
      {}
    );
  }

  @UserNotify({
    successMessage: marker("Preventivo eliminato"),
    errorMessage: marker("Impossibile eliminare il preventivo")
  })
  delete(id: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.snoots.api.billing}/quotes/${id}`
    );
  }

  @UserNotify({
    successMessage: marker("Preventivo accettato"),
    errorMessage: marker("Impossibile accettare il preventivo")
  })
  accept(quoteId: string): Observable<void> {
    return this.http.put<void>(API.BILLING.QUOTES.ID_ACCEPT(quoteId), {});
  }

  @UserNotify({
    successMessage: marker("Preventivo rifiutato"),
    errorMessage: marker("Impossibile rifiutare il preventivo")
  })
  reject(quoteId: string): Observable<void> {
    return this.http.put<void>(API.BILLING.QUOTES.ID_REJECT(quoteId), {});
  }

  @UserNotify({
    successMessage: marker("Preventivo in BOZZA"),
    errorMessage: marker("Impossibile sbloccare il preventivo")
  })
  unAccept(quoteId: string): Observable<void> {
    return this.http.put<void>(API.BILLING.QUOTES.ID_UNACCEPT(quoteId), {});
  }

  @UserNotify({
    successMessage: marker("Preventivo in BOZZA"),
    errorMessage: marker("Impossibile sbloccare il preventivo")
  })
  unReject(quoteId: string): Observable<void> {
    return this.http.put<void>(API.BILLING.QUOTES.ID_UNREJECT(quoteId), {});
  }

  @UserNotify({
    successMessage: (id: string, item: QuoteItemCreate) =>
      `${item.name} ${sntTranslate(marker("aggiunto al preventivo"))}`,
    errorMessage: marker("Impossibile aggiungere al preventivo")
  })
  addItem(id: string, quoteItemCreate: QuoteItemCreate): Observable<QuoteItem> {
    return this.addItemSilently(id, quoteItemCreate);
  }

  addItemSilently(
    id: string,
    quoteItemCreate: QuoteItemCreate
  ): Observable<QuoteItem> {
    return this.http.post<QuoteItem>(
      `${environment.snoots.api.billing}/quotes/${id}/items`,
      quoteItemCreate
    );
  }

  @UserNotify({
    successMessage: (id: string, item: Partial<QuoteItem>) =>
      `${item.name} ${sntTranslate(marker("rimosso"))}`,
    errorMessage: marker("Impossibile rimuovere la riga")
  })
  removeItem(id: string, item: QuoteItem): Observable<void> {
    return this.http.delete<void>(
      `${environment.snoots.api.billing}/quotes/${id}/items/${item.id}`
    );
  }

  @UserNotify({
    successMessage: (
      id: string,
      itemId: string,
      itemName: string,
      item: Partial<QuoteItem>
    ) => `${itemName} ${sntTranslate(marker("aggiornato"))}`,
    errorMessage: marker("Impossibile aggiornare la riga")
  })
  updateItem(
    id: string,
    itemId: string,
    itemName: string,
    item: Partial<QuoteItem>
  ): Observable<void> {
    return this.updateItemSilently(id, itemId, item);
  }

  updateItemSilently(
    id: string,
    itemId: string,
    item: Partial<QuoteItem>
  ): Observable<void> {
    return this.http.patch<void>(
      `${environment.snoots.api.billing}/quotes/${id}/items/${itemId}`,
      item
    );
  }

  addAttachment(
    id: string,
    payload: AttachmentV2Create
  ): Observable<AttachmentV2> {
    return this.http.post<AttachmentV2>(
      `${environment.snoots.api.billing}/quotes/${id}/attachments`,
      payload
    );
  }

  loadAttachments(id: string): Observable<AttachmentV2[]> {
    return this.http
      .get<AttachmentV2[]>(
        `${environment.snoots.api.billing}/quotes/${id}/attachments`
      )
      .pipe(
        map((attachments: AttachmentV2[]) => {
          return attachments.map((x) => ({
            ...x,
            extension: x.name && x.name.endsWith(".pdf") ? "pdf" : undefined
          }));
        }),
        map((attachments: AttachmentV2[]) =>
          attachments.sort((left, right) =>
            dateUtil(left.createdAt).isBefore(dateUtil(right.createdAt))
              ? 1
              : -1
          )
        )
      );
  }

  loadMisalignedPriceItems(
    quote: Quote
  ): Observable<Array<{ itemId: string; rateTableUnitPrice: number }>> {
    if (!quote.items || quote.items.length === 0) {
      return of([]);
    }

    const defaultRateTable$ = this.ratetableBackend
      .loadDefault()
      .pipe(shareReplay({ bufferSize: 1, refCount: true }));

    const itemChecks = quote.items
      .filter((item: QuoteItem) => !!item.id && !!item.rateTableItemId)
      .map((item: QuoteItem) => {
        const loadBasePriceAndRateTable$ = ((): Observable<{
          baseUnitPrice: number;
          coefficient: number;
        }> => {
          switch (item.type) {
            case "medical-service":
              return this.medicalServiceBackend
                .loadLight(item.rateTableItemId)
                .pipe(
                  switchMap((ms: MedicalServiceLight) =>
                    this.ratetableBackend.loadLight(ms.rateTableId).pipe(
                      map((rateTable: RateTableLight) => ({
                        baseUnitPrice: ms.unitPrice,
                        coefficient: rateTable.serviceModifier?.coefficient ?? 0
                      })),
                      catchError(() =>
                        of({ baseUnitPrice: ms.unitPrice, coefficient: 0 })
                      )
                    )
                  ),
                  catchError(() => of({ baseUnitPrice: NaN, coefficient: 0 }))
                );
            case "drug-assignment":
              return this.drugBackend.loadLight(item.rateTableItemId).pipe(
                switchMap((drug: DrugLight) =>
                  (item.rateTable?.id
                    ? this.ratetableBackend.loadLight(item.rateTable.id)
                    : defaultRateTable$
                  ).pipe(
                    map((rateTable: RateTableLight | undefined) => ({
                      baseUnitPrice: drug.unitPrice,
                      coefficient: rateTable?.drugModifier?.coefficient ?? 0
                    })),
                    catchError(() =>
                      of({ baseUnitPrice: drug.unitPrice, coefficient: 0 })
                    )
                  )
                ),
                catchError(() => of({ baseUnitPrice: NaN, coefficient: 0 }))
              );
            case "parapharmaceutical":
              return this.drugBackend.loadLight(item.rateTableItemId).pipe(
                switchMap((drug: DrugLight) =>
                  (item.rateTable?.id
                    ? this.ratetableBackend.loadLight(item.rateTable.id)
                    : defaultRateTable$
                  ).pipe(
                    map((rateTable: RateTableLight | undefined) => ({
                      baseUnitPrice: drug.unitPrice,
                      coefficient:
                        rateTable?.parapharmaceuticalModifier?.coefficient ?? 0
                    })),
                    catchError(() =>
                      of({ baseUnitPrice: drug.unitPrice, coefficient: 0 })
                    )
                  )
                ),
                catchError(() => of({ baseUnitPrice: NaN, coefficient: 0 }))
              );
            case "product-sale":
              return this.productBackend.loadLight(item.rateTableItemId).pipe(
                switchMap((product: ProductLight) =>
                  (item.rateTable?.id
                    ? this.ratetableBackend.loadLight(item.rateTable.id)
                    : defaultRateTable$
                  ).pipe(
                    map((rateTable: RateTableLight | undefined) => ({
                      baseUnitPrice: product.unitPrice,
                      coefficient: rateTable?.productModifier?.coefficient ?? 0
                    })),
                    catchError(() =>
                      of({ baseUnitPrice: product.unitPrice, coefficient: 0 })
                    )
                  )
                ),
                catchError(() => of({ baseUnitPrice: NaN, coefficient: 0 }))
              );
            default:
              return of({ baseUnitPrice: NaN, coefficient: 0 });
          }
        })();

        return loadBasePriceAndRateTable$.pipe(
          map(
            (payload: {
              baseUnitPrice: number;
              coefficient: number;
            }): {
              itemId: string;
              rateTableUnitPrice: number;
            } | null => {
              if (Number.isNaN(payload.baseUnitPrice)) {
                return null;
              }

              const rateTableUnitPrice = round(
                payload.baseUnitPrice * (1 + payload.coefficient),
                2
              );
              const quotePriceRounded = round(item.unitPrice, 2);
              const rateTablePriceRounded = round(rateTableUnitPrice, 2);
              const isMisaligned = quotePriceRounded !== rateTablePriceRounded;

              return isMisaligned
                ? { itemId: item.id!, rateTableUnitPrice }
                : null;
            }
          )
        );
      });

    if (itemChecks.length === 0) {
      return of([]);
    }

    return forkJoin(itemChecks).pipe(map((results) => compact(results)));
  }

  @UserNotify({
    successMessage: marker("Prezzi allineati al tarrifario"),
    errorMessage: marker("Impossibile allineare i prezzi al listino")
  })
  alignPricesToRateTable(
    quote: Quote,
    misalignedItemIds: string[]
  ): Observable<void> {
    if (!misalignedItemIds || misalignedItemIds.length === 0) {
      return of(void 0);
    }

    const minMaxEnabled: boolean = !!quote.minMaxEnabled;
    const defaultRateTableForAlign$ = this.ratetableBackend
      .loadDefault()
      .pipe(shareReplay({ bufferSize: 1, refCount: true }));

    const updateOperations = misalignedItemIds
      .map((itemId: string) => quote.items.find((i) => i.id === itemId))
      .filter((item): item is QuoteItem => !!item && !!item.rateTableItemId)
      .map((item: QuoteItem) => {
        const loadPrice$ = ((): Observable<{
          unitPrice: number;
          maxUnitPrice?: number;
        }> => {
          switch (item.type) {
            case "medical-service":
              return this.medicalServiceBackend
                .loadLight(item.rateTableItemId)
                .pipe(
                  switchMap((ms: MedicalServiceLight) =>
                    this.ratetableBackend.loadLight(ms.rateTableId).pipe(
                      map((rateTable: RateTableLight) => {
                        const coefficient =
                          rateTable.serviceModifier?.coefficient ?? 0;

                        return this.medicalServiceBackend
                          .loadLight(
                            item.rateTableItemId,
                            coefficient,
                            ms.rateTableId
                          )
                          .pipe(
                            map((msModified: MedicalServiceLight) => ({
                              unitPrice: msModified.unitPrice,
                              maxUnitPrice:
                                minMaxEnabled &&
                                msModified.maxUnitPrice !== undefined
                                  ? msModified.maxUnitPrice
                                  : undefined
                            }))
                          );
                      }),
                      switchMap((x) => x),
                      catchError(() => of({ unitPrice: NaN }))
                    )
                  ),
                  catchError(() => of({ unitPrice: NaN }))
                );
            case "drug-assignment":
              return this.drugBackend.loadLight(item.rateTableItemId).pipe(
                switchMap((drug: DrugLight) =>
                  (item.rateTable?.id
                    ? this.ratetableBackend.loadLight(item.rateTable.id)
                    : defaultRateTableForAlign$
                  ).pipe(
                    map((rateTable: RateTableLight | undefined) => {
                      const coefficient =
                        rateTable?.drugModifier?.coefficient ?? 0;
                      const unitPrice = round(
                        drug.unitPrice * (1 + coefficient),
                        2
                      );

                      return {
                        unitPrice,
                        maxUnitPrice: minMaxEnabled ? unitPrice : undefined
                      };
                    }),
                    catchError(() =>
                      of({
                        unitPrice: round(drug.unitPrice * (1 + 0), 2),
                        maxUnitPrice: minMaxEnabled
                          ? round(drug.unitPrice * (1 + 0), 2)
                          : undefined
                      })
                    )
                  )
                ),
                catchError(() => of({ unitPrice: NaN }))
              );
            case "parapharmaceutical":
              return this.drugBackend.loadLight(item.rateTableItemId).pipe(
                switchMap((drug: DrugLight) =>
                  (item.rateTable?.id
                    ? this.ratetableBackend.loadLight(item.rateTable.id)
                    : defaultRateTableForAlign$
                  ).pipe(
                    map((rateTable: RateTableLight | undefined) => {
                      const coefficient =
                        rateTable?.parapharmaceuticalModifier?.coefficient ?? 0;
                      const unitPrice = round(
                        drug.unitPrice * (1 + coefficient),
                        2
                      );

                      return {
                        unitPrice,
                        maxUnitPrice: minMaxEnabled ? unitPrice : undefined
                      };
                    }),
                    catchError(() =>
                      of({
                        unitPrice: round(drug.unitPrice * (1 + 0), 2),
                        maxUnitPrice: minMaxEnabled
                          ? round(drug.unitPrice * (1 + 0), 2)
                          : undefined
                      })
                    )
                  )
                ),
                catchError(() => of({ unitPrice: NaN }))
              );
            case "product-sale":
              return (
                item.rateTable?.id
                  ? this.ratetableBackend.loadLight(item.rateTable.id)
                  : defaultRateTableForAlign$
              ).pipe(
                switchMap((rateTable: RateTableLight | undefined) => {
                  const coefficient =
                    rateTable?.productModifier?.coefficient ?? 0;
                  const rateTableId = item.rateTable?.id ?? rateTable?.id;

                  return this.productBackend
                    .loadLight(item.rateTableItemId, coefficient, rateTableId)
                    .pipe(
                      map((product: ProductLight) => ({
                        unitPrice: product.unitPrice,
                        maxUnitPrice:
                          minMaxEnabled && product.maxUnitPrice !== undefined
                            ? product.maxUnitPrice
                            : undefined
                      })),
                      catchError(() => of({ unitPrice: NaN }))
                    );
                }),
                catchError(() => of({ unitPrice: NaN }))
              );
            default:
              return of({ unitPrice: NaN });
          }
        })();

        return loadPrice$.pipe(
          switchMap((prices: { unitPrice: number; maxUnitPrice?: number }) => {
            if (Number.isNaN(prices.unitPrice)) {
              return of(void 0);
            }

            const payload: Partial<QuoteItem> = {
              unitPrice: prices.unitPrice
            };

            if (prices.maxUnitPrice !== undefined) {
              payload.maxUnitPrice = prices.maxUnitPrice;
            }

            return this.updateItemSilently(quote.id, item.id!, payload);
          })
        );
      });

    if (updateOperations.length === 0) {
      return of(void 0);
    }

    return forkJoin(updateOperations).pipe(map((): void => void 0));
  }

  @UserNotify({
    successMessage: marker("Preventivo inviato"),
    errorMessage: marker("Impossibile inviare il preventivo")
  })
  sendAndUpdateEmail(options: {
    quote: Quote;
    email: string;
    emailValueChanged: boolean;
    storageIdToUse?: string;
    note?: string;
  }): Observable<void> {
    const { quote, email, emailValueChanged, storageIdToUse, note } = options;
    return defer(() =>
      iif(
        () => !!storageIdToUse,
        of(storageIdToUse!),
        defer(() => {
          return from(this.toPdf(quote)).pipe(
            switchMap((pdfBase64Blob) => {
              return defer(() =>
                from(this.updateStorage(quote, pdfBase64Blob))
              );
            })
          );
        })
      )
    ).pipe(
      switchMap((storageId: string) => {
        return this.http
          .put(
            `${environment.snoots.api.billing}/quotes/${quote.id}/send`,
            omitBy(
              {
                email,
                notes: note,
                attachments: [
                  {
                    contentType: "application/pdf",
                    fileName: this.fileNamingService.getFileName({
                      fileType: FileType.Quote,
                      context: { quote }
                    }),
                    storageId
                  }
                ]
              },
              isUndefined
            )
          )
          .pipe(
            switchMap(() =>
              iif(
                () => emailValueChanged,
                defer(() =>
                  (async () => {
                    const result = await sntConfirm(
                      sntTranslate(marker("Dati cliente")),
                      sntTranslate(
                        marker(
                          "L'indirizzo email di destinazione è diverso da quello del cliente, vuoi aggiornarlo?"
                        )
                      )
                    );
                    if (result) {
                      await lastValueFrom(
                        this.customerBackend.update(
                          {
                            email
                          },
                          quote.customer.id
                        )
                      );
                    }
                  })()
                ),
                of(void 0)
              )
            )
          );
      })
    );
  }

  async print(quote: Quote, storageIdToUse?: string): Promise<void> {
    let blob;
    try {
      if (storageIdToUse) {
        const downloadUrl = await lastValueFrom(
          this.storageBackend.download(storageIdToUse, "inline")
        );
        blob = (await fetch(downloadUrl)).blob();
      } else {
        blob = this.toPdf(quote);
      }

      await this.printService.printPdf(blob);
    } catch (ex) {
      console.error(ex);
      throw ex;
    }
  }

  getByMessageEvent(quote: Quote, message: MessageEvent<Quote>): Quote {
    return extendDoc({
      ...quote,
      ...message.data
    });
  }

  async updateStorage(quote: Quote, blob: Blob): Promise<string> {
    if (!quote.storageId) {
      const uploadResult = await lastValueFrom(
        this.storageBackend.upload(
          new File(
            [blob],
            this.fileNamingService.getFileName({
              fileType: FileType.Quote,
              context: { quote }
            })
          ),
          null,
          false
        )
      );

      await lastValueFrom(
        this.update(quote.id, {
          storageId: uploadResult.id
        })
      );

      return uploadResult.id;
    } else {
      await lastValueFrom(
        this.storageBackend.update(
          quote.storageId,
          this.fileNamingService.getFileName({
            fileType: FileType.Quote,
            context: { quote }
          }),
          blob
        )
      );
    }

    return quote.storageId;
  }

  async toPdf(quote: Quote): Promise<Blob> {
    const [
      quoteTemplate,
      tailwindCss,
      quoteHeaderTemplate,
      quoteFooterTemplate,
      patient
    ] = await Promise.all([
      fetch("/assets/ejs-templates/quote/quote.ejs").then((response) =>
        response.text()
      ),
      fetch("/assets/ejs-templates/quote/tailwind.css").then((response) =>
        response.text()
      ),
      fetch("/assets/ejs-templates/header.ejs").then((response) =>
        response.text()
      ),
      fetch("/assets/ejs-templates/quote/quote-footer.ejs").then((response) =>
        response.text()
      ),
      quote.patient?.id
        ? lastValueFrom(this.petBackend.loadLight(quote.patient.id))
        : Promise.resolve(undefined)
    ]);

    const configurationBilling = await lastValueFrom(
      this.configurationBackend.loadBilling()
    );

    const viewModel = quoteToQuotePrintViewModel({
      quote,
      patient,
      minMax: configurationBilling.quote?.minMaxEnabled || false,
      regionService: this.regionService,
      moduleService: this.moduleService,
      fileNamingService: this.fileNamingService
    });

    const fiscalProfile = await lastValueFrom(
      this.fiscalProfileBackend.load(quote.fiscalProfile.id)
    );

    let defaultLetterhead;
    if (fiscalProfile.letterheadId) {
      defaultLetterhead = await lastValueFrom(
        this.letterheadBackend.loadLight(fiscalProfile.letterheadId)
      );
    } else {
      defaultLetterhead = await lastValueFrom(
        this.letterheadBackend.loadDefaultLight()
      );
    }

    const { logoData, secondaryImageData } = await lastValueFrom(
      forkJoin({
        logoData: iif(
          () => !!defaultLetterhead?.header?.logo?.id,
          defer(() =>
            this.storageBackend
              .download(defaultLetterhead?.header!.logo!.id!)
              .pipe(
                switchMap((downloadUrl: string) =>
                  from(
                    fetch(downloadUrl)
                      .then((response) => response.blob())
                      .then((blob) => new Blob([blob], { type: "image/png" }))
                      .then((imageBlob) => blobToDataUrl(imageBlob))
                  )
                ),
                catchError(() => of(undefined))
              )
          ),
          of(undefined)
        ),
        secondaryImageData: iif(
          () => !!defaultLetterhead?.header?.secondaryImage?.id,
          defer(() =>
            this.storageBackend
              .download(defaultLetterhead?.header!.secondaryImage!.id!)
              .pipe(
                switchMap((downloadUrl: string) =>
                  from(
                    fetch(downloadUrl)
                      .then((response) => response.blob())
                      .then((blob) => new Blob([blob], { type: "image/png" }))
                      .then((imageBlob) => blobToDataUrl(imageBlob))
                  )
                ),
                catchError(() => of(undefined))
              )
          ),
          of(undefined)
        )
      })
    );

    const htmlHeadeRendered = this.templateService.render(quoteHeaderTemplate, {
      ...viewModel,
      styles: "",
      content: defaultLetterhead?.header?.html || "",
      title: "",
      logo: logoData,
      secondaryImage: secondaryImageData,
      logoSize: defaultLetterhead?.header?.logo?.size || 1,
      secondaryImageSize: defaultLetterhead?.header?.secondaryImage?.size || 1,
      tailwindCss
    });

    const htmlFooterRendered = this.templateService.render(
      quoteFooterTemplate,
      {
        ...viewModel,
        tailwindCss
      }
    );

    const htmlRendered = this.templateService.render(quoteTemplate, {
      ...viewModel,
      tailwindCss
    });

    return await this.printService.htmlToPdf({
      header: htmlHeadeRendered,
      footer: htmlFooterRendered,
      content: htmlRendered
    });
  }
}

function extendLight(quote: QuoteLight): QuoteLight {
  return {
    ...quote,
    status: statusQuote(quote)
  };
}

function extendDoc(quote: Quote): Quote {
  quote.items = quote.items.map((item) => ({
    ...item,
    maxRateable: item.maxRateable || item.rateable,
    maxUnitPrice: item.maxUnitPrice || item.unitPrice
  }));

  const ret = {
    ...quote,
    status: statusQuote(quote),
    discountAmount: quote.taxedTotal - quote.discountedTotal,
    notTaxableTotal:
      !quote.vatSummaryDetails || quote.vatSummaryDetails.length === 0
        ? 0
        : sum(
            quote.vatSummaryDetails
              .filter((x) => !x.tax)
              .map((x) => x.detail.rateable)
          ),
    drugsAssignmentTaxed: sum(
      quote.items
        .filter((x) => x.type === "drug-assignment")
        .map((x) => x.adjustedTotal)
    ),
    drugsAssignmentMaxTaxed: sum(
      quote.items
        .filter((x) => x.type === "drug-assignment")
        .map((x) => x.maxAdjustedTotal || x.adjustedTotal || 0)
    ),
    parapharmaceuticalTaxed: sum(
      quote.items
        .filter((x) => x.type === "parapharmaceutical")
        .map((x) => x.adjustedTotal)
    ),
    parapharmaceuticalMaxTaxed: sum(
      quote.items
        .filter((x) => x.type === "parapharmaceutical")
        .map((x) => x.maxAdjustedTotal || x.adjustedTotal || 0)
    ),
    medicalServicesTaxed: sum(
      quote.items
        .filter((x) => x.type === "medical-service")
        .map((x) => x.adjustedTotal)
    ),
    medicalServicesMaxTaxed: sum(
      quote.items
        .filter((x) => x.type === "medical-service")
        .map((x) => x.maxAdjustedTotal || x.adjustedTotal || 0)
    ),
    productsSaleTaxed: sum(
      quote.items
        .filter((x) => x.type === "product-sale")
        .map((x) => x.adjustedTotal)
    ),
    productsSaleMaxTaxed: sum(
      quote.items
        .filter((x) => x.type === "product-sale")
        .map((x) => x.maxAdjustedTotal || x.adjustedTotal || 0)
    )
  };

  ret.medicalServicesMaxTaxed = Math.max(
    ret.medicalServicesTaxed,
    ret.medicalServicesMaxTaxed
  );

  ret.drugsAssignmentMaxTaxed = Math.max(
    ret.drugsAssignmentTaxed,
    ret.drugsAssignmentMaxTaxed
  );

  ret.parapharmaceuticalMaxTaxed = Math.max(
    ret.parapharmaceuticalTaxed,
    ret.parapharmaceuticalMaxTaxed
  );

  ret.productsSaleMaxTaxed = Math.max(
    ret.productsSaleTaxed,
    ret.productsSaleMaxTaxed
  );

  return ret;
}

function quoteToQuotePrintViewModel({
  quote,
  minMax,
  regionService,
  moduleService,
  fileNamingService,
  patient
}: {
  quote: Quote;
  minMax: boolean | undefined;
  regionService: RegionService;
  moduleService: ModuleService;
  fileNamingService: FileNamingService;
  patient: PetLight | undefined;
}): QuotePrintViewModel {
  const percentFormatter = new Intl.NumberFormat("it-IT", {
    style: "percent",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });

  const address = quote.customer?.address || {
    address: "",
    zipCode: "",
    city: "",
    state: "",
    country: ""
  };

  const isItalianAdditionFieldsEnabled = regionService.get() === Regions.IT;

  const isCustomerAddressEnabled = moduleService.canBeActivatedSync(
    MODULES.INVOICE_CUSTOMER_ADDRESS
  );

  return {
    minMax: minMax,
    quoteNumber: quote.number,
    quoteDate: dateUtil(quote.createdAt).format("DD/MM/YYYY"),

    businessName: quote.fiscalProfile.businessName || "",
    businessAddress: formatAddress(quote.fiscalProfile.address),
    businessVatCode: `P.I. ${quote.fiscalProfile.vatNumber}`,
    businessFiscalCode: `C.F. ${
      quote.fiscalProfile.ssn || quote.fiscalProfile.vatNumber
    }`,

    customerAddressLine1: `${address.address || ""}`,
    customerAddressLine2: `${address.zipCode || ""} - ${address.city || ""} (${
      address.state || ""
    })`,

    customerFiscalCode: quote.customer?.ssn || "",
    customerNominative: getCustomerNominative(quote.customer),
    customerVatCode: quote.customer?.vatNumber || "",
    patientName: quote.patient?.name || "",

    discountedTotal: formatCurrency({
      value: round(quote.discountedTotal, 2),
      regionService
    }),

    adjustedTotal: formatCurrency({
      value: round(quote.adjustedTotal, 2),
      regionService
    }),
    maxAdjustedTotal: minMax
      ? formatCurrency({
          value: round(quote.maxAdjustedTotal, 2),
          regionService
        })
      : undefined,

    withholdingTax: percentFormatter.format(quote.fiscalProfile.withholdingTax),

    withholdingTaxAmount: formatCurrency({
      value: round(-1 * quote.withholdingTaxAmount, 2),
      regionService
    }),

    maxWithholdingTaxAmount: minMax
      ? formatCurrency({
          value: round(-1 * quote.maxWithholdingTaxAmount!, 2),
          regionService
        })
      : undefined,
    hideWithholdingTaxAmount: !quote.withholdingTaxAmount,

    rows: quote.items.map((item) => {
      return {
        description: item.name,
        quantity: `${item.quantity}`,
        maxQuantity: `${item.maxQuantity || item.quantity}`,
        unitPrice: formatCurrency({
          value: round(item.unitPrice, 2),
          regionService
        }),
        maxUnitPrice: minMax
          ? formatCurrency({
              value: round(item.maxUnitPrice!, 2),
              regionService
            })
          : undefined,

        rateable: formatCurrency({
          value: round(item.rateable, 2),
          regionService
        }),
        maxRateable: minMax
          ? formatCurrency({
              value: round(item.maxRateable!, 2),
              regionService
            })
          : undefined,

        netTotal: formatCurrency({
          value: round(item.adjustedTotal, 2),
          regionService
        }),
        maxNetTotal: minMax
          ? formatCurrency({
              value: round(item.maxAdjustedTotal!, 2),
              regionService
            })
          : undefined,
        vat: percentFormatter.format(item.vat),
        discount: item.discount ? percentFormatter.format(item.discount) : ""
      };
    }),

    note: quote.note || "",

    hideRateableTotal: !quote.rateableTotal,

    notTaxableTotal: formatCurrency({
      value: round(quote.notTaxableTotal || 0, 2),
      regionService
    }),
    hideNotTaxableTotal: !quote.notTaxableTotal,

    adjustment: formatCurrency({
      value: round(quote.adjustment || 0, 2),
      regionService
    }),
    hideAdjustement: !quote.adjustment,

    enpav: percentFormatter.format(quote.fiscalProfile.enpav?.rate || 0),

    enpavAmount: formatCurrency({ value: quote.enpavAmount, regionService }),
    maxEnpavAmount: formatCurrency({
      value: quote.maxEnpavAmount,
      regionService
    }),
    hideEnpavAmount: !quote.enpavAmount,

    drugsAssignmentTaxed:
      quote.drugsAssignmentTaxed > 0
        ? formatCurrency({
            value: round(quote.drugsAssignmentTaxed, 2),
            regionService
          })
        : "",

    drugsAssignmentMaxTaxed: minMax
      ? quote.drugsAssignmentMaxTaxed > 0
        ? formatCurrency({
            value: round(quote.drugsAssignmentMaxTaxed, 2),
            regionService
          })
        : ""
      : "",
    parapharmaceuticalTaxed:
      quote.parapharmaceuticalTaxed > 0
        ? formatCurrency({
            value: round(quote.parapharmaceuticalTaxed, 2),
            regionService
          })
        : "",

    parapharmaceuticalMaxTaxed: minMax
      ? quote.parapharmaceuticalMaxTaxed > 0
        ? formatCurrency({
            value: round(quote.parapharmaceuticalMaxTaxed, 2),
            regionService
          })
        : ""
      : "",
    medicalServicesTaxed:
      quote.medicalServicesTaxed > 0
        ? formatCurrency({
            value: round(quote.medicalServicesTaxed, 2),
            regionService
          })
        : "",

    medicalServicesMaxTaxed: minMax
      ? quote.medicalServicesMaxTaxed > 0
        ? formatCurrency({
            value: round(quote.medicalServicesMaxTaxed, 2),
            regionService
          })
        : ""
      : "",
    productsSaleTaxed:
      quote.productsSaleTaxed > 0
        ? formatCurrency({
            value: round(quote.productsSaleTaxed, 2),
            regionService
          })
        : "",

    productsSaleMaxTaxed: minMax
      ? quote.productsSaleMaxTaxed > 0
        ? formatCurrency({
            value: round(quote.productsSaleMaxTaxed, 2),
            regionService
          })
        : ""
      : "",

    flatRateProfile: quote.fiscalProfile.taxRegime === "flat-tax-regime",

    vatSummaryLines: quote.vatSummaryDetails.map((x) => ({
      tax: percentFormatter.format(x.tax),
      detail: {
        minTaxAmount: x.detail.minTaxAmount
          ? formatCurrency({
              value: round(x.detail.minTaxAmount, 2),
              regionService
            })
          : "",
        taxAmount: formatCurrency({
          value: round(x.detail.taxAmount, 2),
          regionService
        }),
        maxTaxAmount: x.detail.maxTaxAmount
          ? formatCurrency({
              value: round(x.detail.maxTaxAmount, 2),
              regionService
            })
          : ""
      }
    })),

    footerName: fileNamingService.getFileName({
      fileType: FileType.Quote,
      context: { quote },
      omitDate: true,
      omitFileExtension: true
    }),

    rateableTotal: formatCurrency({
      value: round(quote.rateableTotal, 2),
      regionService
    }),
    maxRateableTotal: quote.maxRateableTotal
      ? formatCurrency({
          value: round(quote.maxRateableTotal, 2),
          regionService
        })
      : "",
    taxedTotal: formatCurrency({
      value: round(quote.taxedTotal, 2),
      regionService
    }),

    preventivoLabel: sntTranslate(marker("Preventivo")),
    dataLabel: sntTranslate(marker("Data")),
    patientLabel: sntTranslate(marker("Paziente")),
    spettabileLabel: sntTranslate(marker("Spettabile")),
    pIvaLabel: sntTranslate(marker("P.IVA")),
    cFLabel: sntTranslate(marker("C.F.")),
    descrizioneLabel: sntTranslate(marker("Descrizione")),
    qtaLabel: sntTranslate(marker("QTA'")),
    qtaMinLabel: sntTranslate(marker("QTA' min")),
    qtaMaxLabel: sntTranslate(marker("QTA' max")),
    pZoLabel: sntTranslate(marker("P.zo")),
    pZoMin: sntTranslate(marker("P.zo min")),
    pZoMax: sntTranslate(marker("P.zo max")),
    totaleLabel: sntTranslate(marker("Totale")),
    totMinLabel: sntTranslate(marker("Tot min")),
    totMaxLabel: sntTranslate(marker("Tot max")),
    scontoLabel: sntTranslate(marker("Sconto")),
    minimoLabel: sntTranslate(marker("Minimo")),
    massimoLabel: sntTranslate(marker("Massimo")),
    prestazioniLabel: sntTranslate(marker("Prestazioni")),
    prodottiLabel: sntTranslate(marker("Prodotti")),
    farmaciLabel: sntTranslate(marker("Farmaci")),
    parafarmaciLabel: sntTranslate(marker("Parafarmaci")),
    imponibileLabel: sntTranslate(marker("Imponibile")),
    nonImponibileLabel: sntTranslate(marker("Non imponibile")),
    ivaLabel: sntTranslate(marker("IVA")),
    totalePreventivo: sntTranslate(marker("Totale preventivo")),
    noteLabel: sntTranslate(marker("Note")),
    firmaPerAccettazione: sntTranslate(marker("Firma per accettazione")),
    firmaDigitaleDelClienteLabel: sntTranslate(
      marker("Firma smart del cliente")
    ),
    patientMicrochipLabel: sntTranslate(marker("Microchip")),
    patientLastWeightLabel: sntTranslate(marker("Peso")),

    patientMicrochipValue: patient?.microchip || "",
    patientLastWeightValue: patient?.lastWeightDisplay || "",

    enableCustomerAddress: isCustomerAddressEnabled,

    disableEnpavAmount: !isItalianAdditionFieldsEnabled,
    disableRitenutaDAcconto: !isItalianAdditionFieldsEnabled,
    disableFlatRateProfileNotice: !isItalianAdditionFieldsEnabled
  };
}

function statusQuote(quote: QuoteLight): QuoteStatus {
  if (quote.billed) {
    return QuoteStatus.BILLED;
  }

  if (quote.signedAt) {
    return QuoteStatus.SIGNED;
  }

  if (quote.acceptedAt) {
    return QuoteStatus.ACCEPTED;
  }

  if (quote.rejectedAt) {
    return QuoteStatus.REJECTED;
  }

  return QuoteStatus.DRAFT;
}
